/**
 * Google Analytics 4 (gtag.js) — samtykkestyret indlæsning.
 *
 * Måle-id'et kommer fra Google Analytics-connectoren som
 * VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY (fx "G-XXXXXXX").
 * Er den ikke sat, gør modulet ingenting — appen fungerer uændret.
 *
 * Flow:
 *  1. initGa() sætter Consent Mode-defaults og lytter på
 *     'consent:change' fra cookiebanneret.
 *  2. Har brugeren givet samtykke til statistik, indlæses gtag.js én gang
 *     og samtykket opdateres til "granted".
 *  3. Trækkes samtykket tilbage, sættes consent tilbage til "denied", så
 *     Google ikke længere må måle (scriptet kan ikke fjernes igen).
 */

import { getConsent } from '@/components/consent/CookieBanner';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Streamen for minnyeelbil.dk — bruges hvis connector-variablen mangler ved build. */
const FALLBACK_MEASUREMENT_ID = 'G-E3M8GPBCG2';

const MEASUREMENT_ID: string =
  (import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined)?.trim() ||
  FALLBACK_MEASUREMENT_ID;


let scriptLoaded = false;
let initialised = false;

// Vigtigt: gtag.js kræver at der pushes et `arguments`-objekt (ikke et array),
// ellers ignorerer Google kommandoerne og der sendes ingen hits.
function gtag(...args: unknown[]) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // Brug Googles officielle globale gtag-funktion. Ud over at behandle
    // kommandoerne korrekt gør det tagget synligt for Googles tagkontrol.
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
  }
  window.gtag(...args);
}



/** Er GA konfigureret (måle-id findes)? */
export function gaConfigured(): boolean {
  return !!MEASUREMENT_ID;
}

/**
 * Er GA aktivt lige nu? Tagget indlæses kun efter statistik-samtykke.
 */
export function gaActive(): boolean {
  return !!MEASUREMENT_ID && scriptLoaded;
}

/** Har brugeren givet statistik-samtykke (fuld måling med cookies)? */
export function gaConsentGranted(): boolean {
  return !!getConsent()?.statistics;
}

/** Er gtag.js-scriptet indlæst i denne session? */
export function gaScriptLoaded(): boolean {
  return scriptLoaded;
}

/** Måle-id'et der bruges (til admin-status). */
export function gaMeasurementId(): string {
  return MEASUREMENT_ID;
}

function loadScript() {
  if (scriptLoaded || !MEASUREMENT_ID || typeof document === 'undefined') return;
  scriptLoaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  s.addEventListener('load', () => {
    trackPageView(window.location.pathname + window.location.search);
  }, { once: true });
  document.head.appendChild(s);

  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID, {
    // Sidevisninger sendes eksplicit, så SPA- og consent-flowet er entydigt.
    send_page_view: false,
    anonymize_ip: true,
  });
}

function applyConsent() {
  const granted = gaConsentGranted();
  gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  if (granted) loadScript();

}

/** Kaldes én gang ved app-start. */
export function initGa() {
  if (initialised || !MEASUREMENT_ID || typeof window === 'undefined') return;
  initialised = true;

  const statisticsGranted = gaConsentGranted();

  // Consent Mode v2 — respekter også et allerede gemt samtykke ved sidestart.
  gtag('consent', 'default', {
    analytics_storage: statisticsGranted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  gtag('set', 'url_passthrough', true);
  gtag('set', 'ads_data_redaction', true);

  window.addEventListener('consent:change', applyConsent);
  applyConsent();

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(
      `[GA] måle-id: ${MEASUREMENT_ID} | statistik-samtykke: ${gaConsentGranted()}`,
    );
  }
}


/** Sidevisning ved route-skift i SPA'en. */
export function trackPageView(path: string, title?: string) {
  if (!gaActive() || !MEASUREMENT_ID) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    page_title: title ?? (typeof document !== 'undefined' ? document.title : undefined),
  });
}

/** Generisk event — ignoreres hvis GA ikke er aktivt. */
export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (!gaActive()) return;
  gtag('event', name, params ?? {});
}
